/**
 * 拾取服务实现：管理拾取提供者，支持射线拾取、矩形区域拾取和屏幕坐标拾取
 */
import * as THREE from 'three'
import type { PickQuery, PickResult, RectPickQuery, ViewportRect } from '@/shared/types/core'
import type {
  PickingService,
  PickProvider,
  ViewportCamera,
  ViewRegistry,
} from '@/shared/types/services'
import type { ViewId, ViewSnapshot } from '@/shared/types/view'
import { ndcToRay, screenToNDC, toViewportLocalPoint } from '@/shared/utils/index'
import { assertValidPickMinDistance } from '@/shared/utils/pickDepth'

/** 拾取服务实现类 */
export class PickingServiceImpl implements PickingService {
  /** 已注册的拾取提供者集合 */
  private providers = new Set<PickProvider>()

  /** 获取当前视口信息的回调 */
  private getViewport: () => ViewportRect

  /** 获取多视口注册表的回调；单视口兼容路径可为空 */
  private readonly getViewRegistry: (() => ViewRegistry | null | undefined) | null

  /** 获取当前默认 surface DPR；异步查询创建时立即冻结。 */
  private readonly getPixelRatio: () => number

  constructor(
    getViewport: () => ViewportRect,
    getViewRegistry?: () => ViewRegistry | null | undefined,
    getPixelRatio?: () => number
  ) {
    this.getViewport = getViewport
    this.getViewRegistry = getViewRegistry ?? null
    this.getPixelRatio = getPixelRatio ?? (() => 1)
  }

  /**
   * 注册拾取提供者。
   *
   * @param provider - 拾取提供者
   */
  addProvider(provider: PickProvider): void {
    this.providers.add(provider)
  }

  /**
   * 移除拾取提供者。
   *
   * @param provider - 拾取提供者；不存在时忽略
   */
  removeProvider(provider: PickProvider): void {
    this.providers.delete(provider)
  }

  /**
   * 射线拾取：遍历所有符合条件的 provider，合并结果按 distance 升序排序。
   *
   * @param query - 拾取查询
   * @returns 命中结果数组，按距离升序排序
   */
  pick(query: PickQuery): PickResult[] {
    assertValidPickMinDistance(query.minDistance)
    return this.withQueryLayerMask(query.camera, query.layerMask, () => {
      const results: PickResult[] = []
      for (const provider of Array.from(this.providers)) {
        if (!matchKinds(provider, query.kinds)) continue
        const result = provider.pick(query)
        if (result) {
          results.push(result)
        }
      }
      results.sort((a, b) => a.distance - b.distance)
      return results
    })
  }

  async pickAsync(query: PickQuery): Promise<PickResult[]> {
    const snapshot = clonePickQuery(query)
    throwIfAborted(snapshot.signal)
    const providers = Array.from(this.providers).filter((provider) =>
      matchKinds(provider, snapshot.kinds)
    )
    const results = await Promise.all(
      providers.map(async (provider) => {
        throwIfAborted(snapshot.signal)
        if (provider.pickAsync) {
          return await awaitWithAbort(provider.pickAsync(snapshot), snapshot.signal)
        }
        if (snapshot.gpuOnly) return null
        return this.withQueryLayerMask(snapshot.camera, snapshot.layerMask, () =>
          provider.pick(snapshot)
        )
      })
    )
    throwIfAborted(snapshot.signal)
    return results
      .filter((result): result is PickResult => result !== null)
      .sort((a, b) => a.distance - b.distance)
  }

  /**
   * 矩形区域拾取：只调用实现了 pickRect 方法的 provider，合并结果按 distance 升序排序。
   *
   * @param query - 矩形拾取查询
   * @returns 命中结果数组，按距离升序排序
   */
  pickRect(query: RectPickQuery): PickResult[] {
    return this.withQueryLayerMask(query.camera, query.layerMask, () => {
      const results: PickResult[] = []
      for (const provider of Array.from(this.providers)) {
        if (!matchKinds(provider, query.kinds)) continue
        if (!provider.pickRect) continue
        const partial = provider.pickRect(query)
        for (const r of partial) {
          results.push(r)
        }
      }
      results.sort((a, b) => a.distance - b.distance)
      return results
    })
  }

  async pickRectAsync(query: RectPickQuery): Promise<PickResult[]> {
    const snapshot = cloneRectPickQuery(query)
    throwIfAborted(snapshot.signal)
    const maxResults =
      snapshot.maxResults === undefined ? undefined : normalizeMaxResults(snapshot.maxResults)
    const providers = Array.from(this.providers).filter((provider) =>
      matchKinds(provider, snapshot.kinds)
    )
    const results = await Promise.all(
      providers.map(async (provider) => {
        throwIfAborted(snapshot.signal)
        if (provider.pickRectAsync) {
          return await awaitWithAbort(provider.pickRectAsync(snapshot), snapshot.signal)
        }
        if (snapshot.gpuOnly || !provider.pickRect) return []
        return this.withQueryLayerMask(snapshot.camera, snapshot.layerMask, () =>
          provider.pickRect!(snapshot)
        )
      })
    )
    throwIfAborted(snapshot.signal)
    const flattened = results.flat().sort((a, b) => a.distance - b.distance)
    return maxResults === undefined ? flattened : flattened.slice(0, maxResults)
  }

  /**
   * 从指定视图的局部屏幕坐标执行拾取。
   *
   * @param viewId - 视图 ID
   * @param screen - 视图局部屏幕坐标，单位像素
   * @param options - 额外查询参数
   * @returns 命中结果数组；视图不可拾取返回空数组
   */
  pickInView(
    viewId: ViewId,
    screen: THREE.Vector2,
    options: Partial<PickQuery> = {}
  ): PickResult[] {
    const registry = this.getViewRegistry?.() ?? null
    const view = registry?.get(viewId)
    if (!this.isPickableView(view)) {
      return []
    }

    const screenInContainer = new THREE.Vector2(view.rect.x + screen.x, view.rect.y + screen.y)
    const camera = this.resolveCamera(
      registry,
      viewId,
      options.camera ?? new THREE.PerspectiveCamera()
    )

    return this.pickAtScreen(screenInContainer, camera, {
      ...options,
      viewId,
    })
  }

  async pickInViewAsync(
    viewId: ViewId,
    screen: THREE.Vector2,
    options: Partial<PickQuery> = {}
  ): Promise<PickResult[]> {
    throwIfAborted(options.signal)
    const registry = this.getViewRegistry?.() ?? null
    const view = registry?.get(viewId)
    if (!this.isPickableView(view)) return []

    const screenInContainer = new THREE.Vector2(view.rect.x + screen.x, view.rect.y + screen.y)
    const camera = this.resolveCamera(
      registry,
      viewId,
      options.camera ?? new THREE.PerspectiveCamera()
    )
    return this.pickAtScreenAsync(screenInContainer, camera, {
      ...options,
      viewId,
    })
  }

  /**
   * 从屏幕坐标构造射线并执行拾取。
   *
   * @param screen - 屏幕坐标，单位像素；具体作用域由 options 决定
   * @param camera - 生成射线用的相机
   * @param options - 额外查询参数
   * @returns 命中结果数组
   */
  pickAtScreen(
    screen: THREE.Vector2,
    camera: THREE.Camera,
    options: Partial<PickQuery> = {}
  ): PickResult[] {
    const containerViewport = this.cloneRect(this.getViewport())
    const screenInContainer = this.normalizeScreen(screen, options, containerViewport)
    const context = this.resolvePickContext(screenInContainer, camera, options, containerViewport)
    if (context.viewport.width <= 0 || context.viewport.height <= 0) return []

    context.camera.updateMatrixWorld(true)

    const ndc = screenToNDC(screenInContainer, context.viewport)
    const ray = ndcToRay(ndc, context.camera)
    const query: PickQuery = {
      ...options,
      screen: screenInContainer,
      viewport: this.cloneRect(context.viewport),
      viewId: context.viewId,
      viewRect: context.viewRect ? this.cloneRect(context.viewRect) : undefined,
      layerMask: context.layerMask,
      pixelRatio: normalizePixelRatio(options.pixelRatio ?? this.getPixelRatio()),
      ndc,
      ray,
      camera: context.camera,
    }
    return this.pick(query)
  }

  async pickAtScreenAsync(
    screen: THREE.Vector2,
    camera: THREE.Camera,
    options: Partial<PickQuery> = {}
  ): Promise<PickResult[]> {
    throwIfAborted(options.signal)
    const containerViewport = this.cloneRect(this.getViewport())
    const screenInContainer = this.normalizeScreen(screen, options, containerViewport)
    const context = this.resolvePickContext(screenInContainer, camera, options, containerViewport)
    if (context.viewport.width <= 0 || context.viewport.height <= 0) return []

    context.camera.updateMatrixWorld(true)
    const ndc = screenToNDC(screenInContainer, context.viewport)
    const ray = ndcToRay(ndc, context.camera)
    const query: PickQuery = {
      ...options,
      screen: screenInContainer,
      viewport: this.cloneRect(context.viewport),
      viewId: context.viewId,
      viewRect: context.viewRect ? this.cloneRect(context.viewRect) : undefined,
      layerMask: context.layerMask,
      pixelRatio: normalizePixelRatio(options.pixelRatio ?? this.getPixelRatio()),
      ndc,
      ray,
      camera: cloneCameraSnapshot(context.camera, context.layerMask),
    }
    return this.pickAsync(query)
  }

  /** 释放资源，清空提供者列表 */
  dispose(): void {
    this.providers.clear()
  }

  private resolvePickContext(
    screen: THREE.Vector2,
    fallbackCamera: THREE.Camera,
    options: Partial<PickQuery>,
    containerViewport: ViewportRect
  ): {
    camera: THREE.Camera
    viewport: ViewportRect
    viewId?: ViewId
    viewRect?: ViewportRect
    layerMask?: number
  } {
    const registry = this.getViewRegistry?.() ?? null
    const resolvedView = this.resolveViewSnapshot(registry, screen, options.viewId)
    if (options.viewId && registry && !resolvedView) {
      return {
        camera: fallbackCamera,
        viewport: { x: 0, y: 0, width: 0, height: 0 },
        viewId: options.viewId,
      }
    }

    const explicitViewRect = options.viewRect ?? options.viewport
    const viewRect = resolvedView
      ? this.cloneRect(resolvedView.rect)
      : explicitViewRect
        ? this.cloneRect(explicitViewRect)
        : undefined

    return {
      camera: this.resolveCamera(registry, resolvedView?.id, fallbackCamera),
      viewport: viewRect ?? {
        x: 0,
        y: 0,
        width: containerViewport.width,
        height: containerViewport.height,
      },
      viewId: resolvedView?.id ?? options.viewId,
      viewRect,
      layerMask: resolvedView?.layerMask ?? options.layerMask,
    }
  }

  private resolveViewSnapshot(
    registry: ViewRegistry | null,
    screen: THREE.Vector2,
    requestedViewId?: ViewId
  ): ViewSnapshot | null {
    if (!registry) {
      return null
    }

    if (requestedViewId) {
      const requestedView = registry.get(requestedViewId)
      return this.isPickableView(requestedView) ? requestedView : null
    }

    const hitView = registry.resolveViewAtScreen(screen)
    if (this.isPickableView(hitView)) {
      return hitView
    }

    const activeView = registry.getActive()
    return this.isPickableView(activeView) ? activeView : null
  }

  private resolveCamera(
    registry: ViewRegistry | null,
    viewId: ViewId | undefined,
    fallbackCamera: THREE.Camera
  ): THREE.Camera {
    if (!registry || !viewId) {
      return fallbackCamera
    }

    const managedCamera = registry.getExtension(viewId, 'viewportCamera') as
      | ViewportCamera
      | undefined
    return managedCamera?.getActiveCamera() ?? fallbackCamera
  }

  private withQueryLayerMask<T>(
    camera: THREE.Camera,
    layerMask: number | undefined,
    action: () => T
  ): T {
    if (layerMask === undefined) {
      return action()
    }

    const previousMask = camera.layers.mask
    camera.layers.mask = layerMask
    try {
      return action()
    } finally {
      camera.layers.mask = previousMask
    }
  }

  private isPickableView(view: ViewSnapshot | undefined): view is ViewSnapshot {
    return !!view && view.visible && view.rect.width > 0 && view.rect.height > 0
  }

  private cloneRect(rect: { x: number; y: number; width: number; height: number }): ViewportRect {
    return {
      x: rect.x,
      y: rect.y,
      width: rect.width,
      height: rect.height,
    }
  }

  private normalizeScreen(
    screen: THREE.Vector2,
    options: Partial<PickQuery>,
    containerViewport: ViewportRect
  ): THREE.Vector2 {
    if (options.viewId || options.viewRect || options.viewport) {
      return screen.clone()
    }

    return toViewportLocalPoint(screen, containerViewport)
  }
}

function throwIfAborted(signal: AbortSignal | undefined): void {
  if (!signal?.aborted) return
  throw signal.reason ?? new DOMException('拾取请求已取消', 'AbortError')
}

function clonePickQuery(query: PickQuery): PickQuery {
  return {
    ...query,
    ndc: query.ndc.clone(),
    ray: query.ray.clone(),
    camera: cloneCameraSnapshot(query.camera, query.layerMask),
    screen: query.screen?.clone(),
    viewport: query.viewport ? { ...query.viewport } : undefined,
    viewRect: query.viewRect ? { ...query.viewRect } : undefined,
    kinds: query.kinds ? [...query.kinds] : undefined,
    assetIds: query.assetIds ? [...query.assetIds] : undefined,
  }
}

function cloneRectPickQuery(query: RectPickQuery): RectPickQuery {
  return {
    ...query,
    min: query.min.clone(),
    max: query.max.clone(),
    camera: cloneCameraSnapshot(query.camera, query.layerMask),
    viewport: query.viewport ? { ...query.viewport } : undefined,
    viewRect: query.viewRect ? { ...query.viewRect } : undefined,
    kinds: query.kinds ? [...query.kinds] : undefined,
  }
}

function cloneCameraSnapshot(camera: THREE.Camera, layerMask?: number): THREE.Camera {
  const snapshot = camera.clone()
  snapshot.matrixWorld.copy(camera.matrixWorld)
  snapshot.matrixWorldInverse.copy(camera.matrixWorldInverse)
  snapshot.projectionMatrix.copy(camera.projectionMatrix)
  snapshot.projectionMatrixInverse.copy(camera.projectionMatrixInverse)
  if (layerMask !== undefined) {
    snapshot.layers.mask = layerMask
  }
  snapshot.matrixAutoUpdate = false
  snapshot.matrixWorldAutoUpdate = false
  return snapshot
}

async function awaitWithAbort<T>(promise: Promise<T>, signal: AbortSignal | undefined): Promise<T> {
  if (!signal) return promise
  throwIfAborted(signal)
  let onAbort: (() => void) | undefined
  const aborted = new Promise<never>((_, reject) => {
    onAbort = () => reject(signal.reason ?? new DOMException('拾取请求已取消', 'AbortError'))
    signal.addEventListener('abort', onAbort, { once: true })
  })
  try {
    return await Promise.race([promise, aborted])
  } finally {
    if (onAbort) signal.removeEventListener('abort', onAbort)
  }
}

function normalizeMaxResults(value: number): number {
  if (!Number.isSafeInteger(value) || value < 0) {
    throw new Error(`非法异步矩形拾取 maxResults: ${value}`)
  }
  return value
}

function normalizePixelRatio(value: number): number {
  return Number.isFinite(value) && value > 0 ? value : 1
}

/** 判断 provider 是否匹配 kinds 过滤条件；kinds 为空或未提供时匹配所有 */
function matchKinds(provider: PickProvider, kinds: PickQuery['kinds']): boolean {
  if (!kinds || kinds.length === 0) return true
  return kinds.includes(provider.kind)
}
